// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  TransferInstructionContextType,
  useTransferInstructionContext,
} from '@hooks/mutations/allocation/useTransferInstructionContext';
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import {
  TransferInstruction,
  TransferInstruction_Accept,
} from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import { ContractId } from '@daml/types';
import { toTokenStandardDisclosedContract } from '../../../../utils/common';
import { ERR_ACC_INSTRUCT_XFER, SUC_ACC_INSTRUCT_XFER } from '../../../../utils/strings';

interface Args {
  registrar: string;
  transferCid: ContractId<TransferInstruction>;
}
export const useAcceptTransferInstruction = () => {
  const { submit: acceptTransferInstruction } = useExerciseSubmission(
    TransferInstruction.TransferInstruction_Accept,
    SUC_ACC_INSTRUCT_XFER,
    ERR_ACC_INSTRUCT_XFER,
  );
  const getTransferInstructionAcceptContext = useTransferInstructionContext(
    TransferInstructionContextType.Accept,
  );
  const accept = async (args: Args) => {
    const { registrar, transferCid } = args;
    const acceptTransferInstructionContext = await getTransferInstructionAcceptContext.mutateAsync({
      registrar,
      transferInstructionCid: transferCid,
    });

    const payload: TransferInstruction_Accept = {
      extraArgs: {
        context: acceptTransferInstructionContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };
    const disclosedContracts =
      acceptTransferInstructionContext.disclosedContracts.map(toTokenStandardDisclosedContract) ||
      [];
    await acceptTransferInstruction({
      cid: transferCid,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
  return accept;
};
